import React, { useRef, useState } from "react";
import api, { errMsg } from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Mic, Square, Loader2, Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable voice-fill control:
 *   1. Click → start recording (MediaRecorder, audio/webm)
 *   2. Click again → stop, send to /voice-fill/transcribe (Whisper)
 *   3. Then send transcript + form_type to /voice-fill/parse (Claude → JSON)
 *   4. Show parsed fields in a preview dialog
 *   5. On confirm → onParsed(fields) is called so the parent form patches its state
 *
 * Props: formType ("task"|"invoice"|"note"), onParsed(fields), label, lang
 */
export default function VoiceFillButton({ formType, onParsed, label, lang = "de" }) {
    const [state, setState] = useState("idle");  // idle | recording | processing | preview
    const [transcript, setTranscript] = useState("");
    const [fields, setFields] = useState({});
    const recRef = useRef(null);
    const chunksRef = useRef([]);

    const start = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                if (blob.size === 0) { setState("idle"); return; }
                setState("processing");
                try {
                    const fd = new FormData();
                    fd.append("file", blob, "voice.webm");
                    fd.append("language", lang === "de" ? "de" : "en");
                    const tr = await api.post("/voice-fill/transcribe", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    const text = (tr.data?.text || "").trim();
                    if (!text) { toast(lang === "de" ? "Nichts erkannt" : "Nothing heard"); setState("idle"); return; }
                    setTranscript(text);
                    const pa = await api.post("/voice-fill/parse", { form_type: formType, transcript: text });
                    setFields(pa.data?.fields || {});
                    setState("preview");
                } catch (e) {
                    toast.error(errMsg(e, lang === "de" ? "Sprach-Eingabe fehlgeschlagen" : "Voice fill failed"));
                    setState("idle");
                }
            };
            recRef.current = rec;
            rec.start();
            setState("recording");
        } catch {
            toast.error(lang === "de" ? "Mikrofonzugriff abgelehnt" : "Microphone denied");
        }
    };
    const stop = () => { try { recRef.current?.stop(); } catch { /* already stopped */ } };
    const confirm = () => { onParsed?.(fields, transcript); setState("idle"); setFields({}); setTranscript(""); };
    const cancel = () => { setState("idle"); setFields({}); setTranscript(""); };

    return (
        <>
            <button
                type="button"
                onClick={state === "recording" ? stop : start}
                disabled={state === "processing"}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium transition shadow-sm ${
                    state === "recording" ? "bg-red-600 hover:bg-red-700 text-white claude-rec-pulse"
                                          : "bg-gradient-to-br from-brand-500 to-brand-700 hover:from-brand-600 hover:to-brand-800 text-white"
                }`}
                data-testid={`voice-fill-${formType}-btn`}
                title={lang === "de" ? "Per Sprache ausfüllen" : "Fill by voice"}
            >
                {state === "processing" ? <Loader2 className="w-4 h-4 animate-spin" /> :
                 state === "recording"  ? <Square className="w-4 h-4 fill-white" /> :
                                          <Mic className="w-4 h-4" />}
                <Sparkles className="w-3 h-3" />
                <span className="hidden sm:inline">
                    {state === "recording" ? (lang === "de" ? "Stopp" : "Stop") :
                     state === "processing" ? (lang === "de" ? "verarbeite…" : "processing…") :
                     (label || (lang === "de" ? "Sprach-Eingabe" : "Voice fill"))}
                </span>
            </button>

            <Dialog open={state === "preview"} onOpenChange={(o) => { if (!o) cancel(); }}>
                <DialogContent className="max-w-md" data-testid={`voice-fill-${formType}-preview`}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-brand-600" /> {lang === "de" ? "Vorschlag prüfen" : "Review suggestion"}</DialogTitle>
                        <DialogDescription>
                            {lang === "de" ? "Claude hat das Transkript in Felder zerlegt. Bestätige zum Übernehmen." : "Claude split your transcript into fields. Confirm to apply."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-[11px] text-neutral-500 italic border-l-2 border-brand-200 pl-2">
                            &ldquo;{transcript}&rdquo;
                        </div>
                        <div className="bg-neutral-50 border border-neutral-200 rounded-md p-3 space-y-2 text-sm">
                            {Object.keys(fields).length === 0 && (
                                <p className="text-neutral-500 text-center py-3">
                                    {lang === "de" ? "Keine Felder erkannt." : "No fields detected."}
                                </p>
                            )}
                            {Object.entries(fields).map(([k, v]) => (
                                <div key={k} className="grid grid-cols-[110px_1fr] gap-2 items-start">
                                    <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 pt-0.5">{k}</span>
                                    <span className="font-medium break-words" data-testid={`voice-field-${k}`}>{String(v ?? "")}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={cancel} data-testid={`voice-fill-${formType}-cancel`}>
                            <X className="w-4 h-4 mr-1" /> {lang === "de" ? "Verwerfen" : "Discard"}
                        </Button>
                        <Button onClick={confirm} disabled={Object.keys(fields).length === 0} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid={`voice-fill-${formType}-confirm`}>
                            <Check className="w-4 h-4 mr-1" /> {lang === "de" ? "Übernehmen" : "Apply"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
